import React, { useEffect, useMemo, useState } from "react";

type CampaignSnapshot = {
  views: number;
  spend: number;
  websiteVisits: number;
  leads: number;
};

export default function NexoraXCampaignHub() {
  const [campaignName, setCampaignName] = useState("TikTok Smart Quote Traffic Test");
  const [platform, setPlatform] = useState("TikTok");
  const [videoLink, setVideoLink] = useState("");
  const [trackingUrl, setTrackingUrl] = useState(
    "https://nexoraxdigital.co.uk/smart-quote?utm_source=tiktok&utm_medium=paid_social&utm_campaign=tiktok_smart_quote_traffic_test&utm_content=smart_quote_explainer"
  );

  const [snapshot, setSnapshot] = useState<CampaignSnapshot>({
    views: 55700,
    spend: 15.46,
    websiteVisits: 0,
    leads: 0,
  });

  const ctr = useMemo(() => {
    if (!snapshot.views) return "0.00";
    return ((snapshot.websiteVisits / snapshot.views) * 100).toFixed(2);
  }, [snapshot.views, snapshot.websiteVisits]);

  const costPerVisit = useMemo(() => {
    if (!snapshot.websiteVisits) return "0.00";
    return (snapshot.spend / snapshot.websiteVisits).toFixed(2);
  }, [snapshot.spend, snapshot.websiteVisits]);

  
  const costPerVisitPence = useMemo(() => {
    if (!snapshot.websiteVisits) return "0.00";
    return ((snapshot.spend / snapshot.websiteVisits) * 100).toFixed(2);
  }, [snapshot.spend, snapshot.websiteVisits]);

  const costPerLead = useMemo(() => {
    if (!snapshot.leads) return "0.00";
    return (snapshot.spend / snapshot.leads).toFixed(2);
  }, [snapshot.spend, snapshot.leads]);

  const leadRate = useMemo(() => {
    if (!snapshot.websiteVisits) return "0.00";
    return ((snapshot.leads / snapshot.websiteVisits) * 100).toFixed(2);
  }, [snapshot.websiteVisits, snapshot.leads]);


  useEffect(() => {
    fetch("/campaign-hub-tiktok-summary.json?v=" + Date.now())
      .then((res) => res.json())
      .then((data) => {
        if (!data || !data.success) return;

        setSnapshot((prev) => ({
          ...prev,
          websiteVisits: Number(data.smartQuoteVisits || data.websiteVisits || data.campaignVisits || prev.websiteVisits || 0),
          leads: Number(data.realLeads ?? data.leads ?? 0),
        }));
      })
      .catch(() => {
        // Keep manual values if the live snapshot is unavailable.
      });
  }, []);

  const saveSnapshot = () => {
    const payload = {
      campaignName,
      platform,
      videoLink,
      trackingUrl,
      snapshot,
      ctr,
      costPerVisit,
      costPerLead,
      leadRate,
      savedAt: new Date().toISOString(),
    };

    localStorage.setItem("nexorax_campaign_hub_snapshot", JSON.stringify(payload, null, 2));
    alert("Campaign Hub snapshot saved on this browser.");
  };

  const copyTracking = async () => {
    await navigator.clipboard.writeText(trackingUrl);
    alert("Tracking URL copied.");
  };

  return (
    <div className="min-h-screen bg-[#020817] text-white px-4 py-10">
      <div className="max-w-7xl mx-auto space-y-8">
        <section className="relative overflow-hidden rounded-[2rem] border border-cyan-400/30 bg-white/[0.045] backdrop-blur-2xl shadow-[0_0_80px_rgba(0,255,255,0.12)] p-8 md:p-12">
          <div className="absolute inset-0 bg-gradient-to-br from-cyan-500/10 via-purple-500/10 to-emerald-500/5 pointer-events-none" />
          <div className="relative z-10 grid lg:grid-cols-[1.15fr_0.85fr] gap-8 items-center">
            <div>
              <p className="text-cyan-300 tracking-[0.45em] text-xs font-black uppercase mb-4">
                NexoraX Campaign Intelligence Hub
              </p>
              <h1 className="text-4xl md:text-6xl font-black leading-tight uppercase">
                Track videos, clicks, leads and conversion flow in one command centre.
              </h1>
              <p className="mt-5 text-slate-300 max-w-3xl text-lg">
                Use this hub for TikTok, YouTube Shorts, Instagram Reels, Facebook, QR campaigns and paid traffic.
                Launch Pad prepares the campaign. Campaign Hub watches the results.
              </p>
            </div>

            <div className="rounded-3xl border border-purple-400/30 bg-black/40 p-6 shadow-[0_0_50px_rgba(168,85,247,0.18)]">
              <p className="text-purple-300 text-xs uppercase tracking-[0.35em] font-black">Live Campaign Snapshot</p>
              <h2 className="mt-3 text-2xl font-black text-cyan-200">{campaignName}</h2>
              <div className="mt-5 grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-cyan-500/10 border border-cyan-400/20 p-4">
                  <p className="text-xs text-slate-400 uppercase">Views</p>
                  <p className="text-3xl font-black text-cyan-300">{snapshot.views.toLocaleString()}</p>
                </div>
                <div className="rounded-2xl bg-purple-500/10 border border-purple-400/20 p-4">
                  <p className="text-xs text-slate-400 uppercase">Spend</p>
                  <p className="text-3xl font-black text-purple-300">£{snapshot.spend.toFixed(2)}</p>
                </div>
                <div className="rounded-2xl bg-emerald-500/10 border border-emerald-400/20 p-4">
                  <p className="text-xs text-slate-400 uppercase">Website Visits</p>
                  <p className="text-3xl font-black text-emerald-300">{snapshot.websiteVisits}</p>
                </div>
                <div className="rounded-2xl bg-fuchsia-500/10 border border-fuchsia-400/20 p-4">
                  <p className="text-xs text-slate-400 uppercase">Leads</p>
                  <p className="text-3xl font-black text-fuchsia-300">{snapshot.leads}</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="grid lg:grid-cols-2 gap-6">
          <div className="rounded-[1.75rem] border border-cyan-400/20 bg-black/45 backdrop-blur-xl p-6">
            <p className="text-cyan-300 text-xs uppercase tracking-[0.35em] font-black mb-4">Campaign Control</p>

            <div className="space-y-4">
              <input className="w-full bg-black/60 border border-cyan-400/20 rounded-xl px-4 py-3 outline-none"
                value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="Campaign name" />

              <input className="w-full bg-black/60 border border-cyan-400/20 rounded-xl px-4 py-3 outline-none"
                value={platform} onChange={(e) => setPlatform(e.target.value)} placeholder="Platform" />

              <input className="w-full bg-black/60 border border-cyan-400/20 rounded-xl px-4 py-3 outline-none"
                value={videoLink} onChange={(e) => setVideoLink(e.target.value)} placeholder="TikTok / video link" />

              <textarea className="w-full min-h-[105px] bg-black/60 border border-cyan-400/20 rounded-xl px-4 py-3 outline-none"
                value={trackingUrl} onChange={(e) => setTrackingUrl(e.target.value)} placeholder="Tracking URL" />

              <div className="flex flex-wrap gap-3">
                <button onClick={copyTracking} className="px-5 py-3 rounded-xl bg-cyan-400 text-black font-black">
                  Copy Tracking URL
                </button>
                <a href={trackingUrl} target="_blank" rel="noreferrer"
                  className="px-5 py-3 rounded-xl border border-cyan-400/40 text-cyan-200 font-black">
                  Test Landing Page
                </a>
                <button onClick={saveSnapshot} className="px-5 py-3 rounded-xl border border-purple-400/50 text-purple-200 font-black">
                  Save Snapshot
                </button>
              </div>
            </div>
          </div>

          <div className="rounded-[1.75rem] border border-purple-400/20 bg-black/45 backdrop-blur-xl p-6">
            <p className="text-purple-300 text-xs uppercase tracking-[0.35em] font-black mb-4">Performance Inputs</p>

            <div className="grid grid-cols-2 gap-4">
              {[
                ["Views", "views"],
                ["Spend £", "spend"],
                ["Website Visits", "websiteVisits"],
                ["Real Leads", "leads"],
              ].map(([label, key]) => (
                <label key={key} className="block">
                  <span className="text-xs text-slate-400 uppercase font-bold">{label}</span>
                  <input
                    type="number"
                    className="mt-2 w-full bg-black/60 border border-purple-400/20 rounded-xl px-4 py-3 outline-none"
                    value={(snapshot as any)[key]}
                    onChange={(e) => setSnapshot((prev) => ({ ...prev, [key]: Number(e.target.value) }))}
                  />
                </label>
              ))}
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-cyan-400/20 bg-cyan-500/10 p-4">
                <p className="text-xs uppercase text-slate-400">Click / Visit Rate</p>
                <p className="text-3xl font-black text-cyan-300">{ctr}%</p>
              </div>
              <div className="rounded-2xl border border-emerald-400/20 bg-emerald-500/10 p-4">
                <p className="text-xs uppercase text-slate-400">Cost Per Visit</p>
                <p className="text-3xl font-black text-emerald-300">{costPerVisitPence}p</p>
              </div>
              <div className="rounded-2xl border border-fuchsia-400/20 bg-fuchsia-500/10 p-4">
                <p className="text-xs uppercase text-slate-400">Cost Per Lead</p>
                <p className="text-3xl font-black text-fuchsia-300">£{costPerLead}</p>
              </div>
              <div className="rounded-2xl border border-purple-400/20 bg-purple-500/10 p-4">
                <p className="text-xs uppercase text-slate-400">Lead Rate</p>
                <p className="text-3xl font-black text-purple-300">{leadRate}%</p>
              </div>
            </div>
          </div>
        </section>

        
      {/* CAMPAIGN_HUB_PREMIUM_INTELLIGENCE_STRIP */}
      <section className="rounded-[2rem] border border-cyan-300/25 bg-gradient-to-br from-cyan-500/[0.08] via-purple-500/[0.08] to-black/70 backdrop-blur-2xl p-6 shadow-[0_0_60px_rgba(34,211,238,0.08)] relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-40">
          <div className="absolute -top-24 left-10 h-48 w-48 rounded-full bg-cyan-400/20 blur-3xl" />
          <div className="absolute bottom-0 right-12 h-56 w-56 rounded-full bg-fuchsia-500/20 blur-3xl" />
        </div>

        <div className="relative z-10 flex flex-col gap-6">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
            <div>
              <p className="text-cyan-300 text-xs uppercase tracking-[0.45em] font-black">
                Live Campaign Intelligence
              </p>
              <h2 className="mt-2 text-3xl md:text-5xl font-black uppercase tracking-tight text-white">
                TikTok → Smart Quote → Lead Flow
              </h2>
              <p className="mt-3 text-slate-300 max-w-3xl">
                This panel turns paid traffic into a live client-ready performance view. It tracks views, website visits, spend efficiency and lead movement from one command centre.
              </p>
            </div>

            <div className="rounded-2xl border border-emerald-400/30 bg-emerald-500/10 px-5 py-4">
              <p className="text-[10px] uppercase tracking-[0.35em] text-emerald-200 font-black">
                Server Sync
              </p>
              <p className="text-2xl font-black text-emerald-300">Live</p>
              <p className="text-xs text-slate-400">Auto-refreshes every 5 minutes</p>
            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-3">
            <div className="rounded-2xl border border-cyan-400/25 bg-black/40 p-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-black">Visit Rate</p>
              <p className="mt-2 text-4xl font-black text-cyan-300">{ctr}%</p>
              <p className="mt-1 text-xs text-slate-400">Website visits from video views</p>
            </div>

            <div className="rounded-2xl border border-emerald-400/25 bg-black/40 p-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-black">Cost Per Visit</p>
              <p className="mt-2 text-4xl font-black text-emerald-300">{costPerVisitPence}p</p>
              <p className="mt-1 text-xs text-slate-400">Cost per website visit</p>
            </div>

            <div className="rounded-2xl border border-fuchsia-400/25 bg-black/40 p-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-black">Cost Per Lead</p>
              <p className="mt-2 text-4xl font-black text-fuchsia-300">£{costPerLead}</p>
              <p className="mt-1 text-xs text-slate-400">Lead acquisition cost</p>
            </div>

            <div className="rounded-2xl border border-purple-400/25 bg-black/40 p-5">
              <p className="text-[10px] uppercase tracking-[0.3em] text-slate-400 font-black">Lead Rate</p>
              <p className="mt-2 text-4xl font-black text-purple-300">{leadRate}%</p>
              <p className="mt-1 text-xs text-slate-400">Visits becoming enquiries</p>
            </div>
          </div>

          <div className="grid lg:grid-cols-5 gap-3">
            {[
              ["1", "Video Views", snapshot.views.toLocaleString(), "TikTok attention captured"],
              ["2", "Website Visits", snapshot.websiteVisits.toLocaleString(), "Traffic reached NexoraX"],
              ["3", "Smart Quote", snapshot.websiteVisits.toLocaleString(), "Landing page opened"],
              ["4", "Real Leads", snapshot.leads.toLocaleString(), "Real briefs submitted"],
              ["5", "Next Action", snapshot.leads > 0 ? "Follow Up" : "Improve CTA", snapshot.leads > 0 ? "Move lead to sales" : "Tighten offer and form"]
            ].map(([step, title, value, note]) => (
              <div key={title} className="rounded-2xl border border-white/10 bg-white/[0.035] p-4">
                <div className="flex items-center gap-3">
                  <span className="h-8 w-8 rounded-full border border-cyan-300/40 bg-cyan-400/10 flex items-center justify-center text-cyan-200 font-black text-xs">
                    {step}
                  </span>
                  <p className="text-[10px] uppercase tracking-[0.25em] text-slate-400 font-black">{title}</p>
                </div>
                <p className="mt-3 text-2xl font-black text-white">{value}</p>
                <p className="mt-1 text-xs text-slate-400">{note}</p>
              </div>
            ))}
          </div>

          <div className="rounded-2xl border border-yellow-300/20 bg-yellow-400/[0.06] p-4">
            <p className="text-yellow-200 text-xs uppercase tracking-[0.3em] font-black">
              Conversion Signal
            </p>
            <p className="mt-2 text-slate-200">
              Traffic is strong. The next improvement is converting more Smart Quote visitors into submitted briefs using a sharper offer, faster form path and clearer callback prompt.
            </p>
          </div>
        </div>
      </section>

<section className="rounded-[1.75rem] border border-emerald-400/20 bg-white/[0.035] backdrop-blur-xl p-6">
          <p className="text-emerald-300 text-xs uppercase tracking-[0.35em] font-black mb-3">
            Linked NexoraX Tools
          </p>
          <div className="grid md:grid-cols-4 gap-3">
            {[
              ["Launch Pad", "/campaign-launch-pad", "Prepare campaign, budget and tracking URL."],
              ["Smart Quote", "/smart-quote", "Main paid traffic landing page."],
              ["Client Preview", "/client-preview", "Track customer brief after submission."],
              ["Guides & Videos", "/guides-videos", "Use videos and scripts for campaigns."],
            ].map(([title, url, desc]) => (
              <a key={title} href={url} className="rounded-2xl border border-cyan-400/20 bg-black/40 p-4 hover:border-cyan-300/60 transition">
                <p className="font-black text-cyan-200">{title}</p>
                <p className="text-sm text-slate-400 mt-2">{desc}</p>
              </a>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
